import ExpandableBox from 'components/shared/ExpandableBox'
import { useEffect, useState } from 'react'
import Input from 'components/shared/Input'
import { useCart } from 'store/cart-slice/hooks'
import { useApp } from 'store/steps'
import CodeValid from './CodeValid'
import { FormattedMessage, useIntl } from 'react-intl'
import { catchErrorByStatus, handleError } from 'helpers/errors'
import Button from 'components/shared/Button'
import Box from 'components/shared/Box'
import Link from 'components/shared/Link'
import { useCouponCode } from 'utils/hooks/useCouponCode'
import { useAppSelector } from 'store/hooks'
import Info from 'components/shared/Info'
import CustomInfo from 'components/shared/CustomInfo'
import moment from 'moment'
import { useTicketPromotions } from 'routes/Tickets/utils/useTicketPromotions'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { useDispatch } from 'react-redux'
import {
    setConfirmationModalDetails,
    setIsConditionConfirmed,
    setIsConfirmationModalOpen,
} from 'store/general-slice'
import { useLazyFindPromotionByCouponCodeQuery } from 'store/api-slice/hooks'
import Loader from 'components/shared/Loader'
import { useResolutions } from 'store/resolutions'

interface PromoCodeProps {
    hasCardVoucherLabel?: boolean
}

const PromoCode = ({ hasCardVoucherLabel }: PromoCodeProps) => {
    const dispatch = useDispatch()
    const { isMobile, isTablet } = useResolutions()
    const { isSummerSaleForCreatorVisibleOutside } = usePromotions()
    const { renderActivePromotions } = useTicketPromotions()
    const [promoCode, setPromoCode] = useState('')
    const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null)
    const [isExpandableBoxOpen, setIsExpandableBoxOpen] = useState(false)
    const [codeError, setCodeError] = useState('')

    const { isCouponCodeProcessing, addCoupon, deleteCoupon } = useCouponCode()
    const { formatMessage } = useIntl()
    const { language } = useApp()
    const { refetchCart, cartCreatorType } = useCart()
    const { items, cart } = useAppSelector((state) => state.cart)
    const {
        confirmationModal: { isConditionConfirmed },
    } = useAppSelector((state) => state.general)
    const codeInCart = cart?.promotionCouponCode
    const isCodeInCart = !!codeInCart
    const isBackToSchoolPromoInCart = codeInCart?.toUpperCase() === 'ZABAWA30'
    const isSpringPromoInCart = codeInCart?.toUpperCase() === 'WIOSNA30'
    const isSummerSalePromoCode = promoCode.toUpperCase() === 'LATO50'

    const { cabanas, tickets } = items
    const hasTodaysCabanas = cabanas.filter((cabana) => {
        if (!cabana.detail.date) return false
        return cabana.detail.date === moment().format('YYYY-MM-DD')
    }).length
    const hasTodaysTickets = tickets.filter((ticket) => {
        if (!ticket.detail.date) return false
        return ticket.detail.date === moment().format('YYYY-MM-DD')
    }).length
    const isNotCompatibleSummerSaleItemInCart =
        isSummerSaleForCreatorVisibleOutside &&
        isSummerSalePromoCode &&
        (hasTodaysTickets || hasTodaysCabanas)

    const [
        triggerFindPromotion,
        { isLoading: isPromotionLoading, isFetching },
    ] = useLazyFindPromotionByCouponCodeQuery()
    const isLoading = isPromotionLoading || isFetching || isCouponCodeProcessing

    const getErrorMessage = (err: unknown) => {
        const { messages, message } = err as MessageErrorModel
        setCodeError(message || messages[0].message)
    }

    const handleChangePromoCode = (code: string) => {
        setPromoCode(code)
        setIsCodeValid(null)
    }

    const verifyCoupon = async () => {
        try {
            const verifyResponse = await triggerFindPromotion({
                _locale: language,
                couponCode: promoCode,
                cartCreatorType,
            })
            handleError(verifyResponse)

            const { confirmation } = verifyResponse.data || {}
            if (confirmation && confirmation.active) {
                dispatch(setConfirmationModalDetails(confirmation))
                dispatch(setIsConfirmationModalOpen(true))
            } else {
                dispatch(setIsConditionConfirmed(true))
            }
        } catch (error) {
            if (
                !catchErrorByStatus({
                    error: error as MessageErrorModel,
                })
            ) {
                setIsCodeValid(false)
                getErrorMessage(error)
            }
        }
    }

    const postCoupon = async () => {
        try {
            const postResponse = await addCoupon({
                _locale: language,
                addPromotionCouponCommand: {
                    couponCode: promoCode,
                },
                cartCreatorType,
            })
            handleError(postResponse)
            setIsCodeValid(true)
            refetchCart()
        } catch (error) {
            if (
                !catchErrorByStatus({
                    error: error as MessageErrorModel,
                })
            ) {
                dispatch(setIsConditionConfirmed(false))
                setIsCodeValid(false)
                getErrorMessage(error)
            }
        }
    }

    const handleRemoveCoupon = async () => {
        dispatch(setIsConditionConfirmed(false))
        try {
            const removeResponse = await deleteCoupon({
                _locale: language,
                cartCreatorType,
            })
            handleError(removeResponse)
            handleChangePromoCode('')
            setIsExpandableBoxOpen(false)
            setIsCodeValid(null)
            refetchCart()
        } catch (error) {
            catchErrorByStatus({
                error: error as MessageErrorModel,
            })
        }
    }

    const handleVerifyCoupon = async () => {
        if (promoCode.length === 0) return setIsCodeValid(false)
        setIsCodeValid(null)

        if (codeInCart) await handleRemoveCoupon()
        await verifyCoupon()
    }

    useEffect(() => {
        if (isCodeValid === null) setCodeError('')
    }, [isCodeValid])

    useEffect(() => {
        if (isCodeInCart) {
            setIsCodeValid(true)
            setPromoCode(codeInCart)
            setIsExpandableBoxOpen(true)
        } else {
            setIsCodeValid(null)
            setPromoCode('')
            setCodeError('')
            setIsExpandableBoxOpen(false)
        }
    }, [isCodeInCart])

    useEffect(() => {
        if (isConditionConfirmed && !isCodeInCart) {
            postCoupon()
        }
    }, [isConditionConfirmed, isCodeInCart])

    useEffect(
        () => () => {
            // clear data for confirmation modal and corresponding consent when leaving the component
            dispatch(setIsConditionConfirmed(false))
            dispatch(setConfirmationModalDetails({}))
        },
        []
    )

    return (
        <Box mt={[8, 0]} isFullWidth>
            {renderActivePromotions({ key: 'discountCode' })}
            {renderActivePromotions({
                key: 'discountCode',
                sectionType: 'unlimited',
            })}
            {!isCodeValid ? (
                <ExpandableBox
                    title={
                        hasCardVoucherLabel
                            ? formatMessage({
                                  defaultMessage:
                                      'Mam kod rabatowy/voucher na kartę',
                                  id: 'vouchers.discount.section_trigger_label_code',
                              })
                            : formatMessage({
                                  defaultMessage: 'Mam kod rabatowy',
                                  id: 'order.discount.section_trigger_label',
                              })
                    }
                    color="secondary_500"
                    maxHeight={350}
                    isBoxOpen={isExpandableBoxOpen}
                    setIsBoxOpen={setIsExpandableBoxOpen}
                    testIdPrefix="payment-trigger-promo-code"
                    marginTop={16}
                >
                    <Box
                        direction={['column', 'row']}
                        gap={[16, 24]}
                        isFullWidth
                    >
                        <Input
                            id="promoCode"
                            type="text"
                            onChange={handleChangePromoCode}
                            placeholder={formatMessage({
                                defaultMessage: 'Kod rabatowy',
                                id: 'order.discount.code_placeholder',
                            })}
                            maxWidth={510}
                            hasNoMaxWidth
                            isFullWidth={isMobile || isTablet}
                            inputLabelFullWidth
                            isError={isLoading ? false : isCodeValid === false}
                            errorMessage={
                                !isLoading
                                    ? codeError ||
                                      formatMessage({
                                          id: 'order.discount.code_error',
                                          defaultMessage:
                                              'Podany kod jest nieprawidłowy.',
                                      })
                                    : ''
                            }
                            value={promoCode}
                            testId="payment-field-promo-code"
                            isFocusable={isExpandableBoxOpen}
                        />
                        <Button
                            isDisabled={isLoading}
                            variant="borderPrimary"
                            width={['100%', '180px']}
                            hasArrow={false}
                            onClick={
                                isCodeValid
                                    ? () => handleRemoveCoupon()
                                    : () => handleVerifyCoupon()
                            }
                            isLoading={isLoading}
                            hasNoWrap
                            testId={`payment-button-promo-code-${
                                isCodeValid ? 'delete' : 'add'
                            }`}
                            isFocusable={isExpandableBoxOpen}
                        >
                            {isCodeValid ? (
                                <FormattedMessage
                                    id="order.discount.btn_remove_caption"
                                    defaultMessage="Usuń"
                                />
                            ) : (
                                <FormattedMessage
                                    id="global.btn.apply"
                                    defaultMessage="Zastosuj"
                                />
                            )}
                        </Button>
                    </Box>

                    {!!isNotCompatibleSummerSaleItemInCart && (
                        <Box mt={16} mb={16}>
                            <CustomInfo
                                iconColor="primary_500"
                                textColor="grayscale_900"
                                backgroundColor="primary_100"
                            >
                                <FormattedMessage
                                    id="order.discount.summer_sale_info"
                                    defaultMessage="Promocja dotyczy tylko biletów i/lub Caban zakupionych na co najmniej jeden dzień przed datą ich wykorzystania."
                                />
                            </CustomInfo>
                        </Box>
                    )}

                    {isBackToSchoolPromoInCart && (
                        <Box mt={8} mb={16}>
                            <Info color="info">
                                <FormattedMessage
                                    id="order.discount.back_to_school_info"
                                    defaultMessage="Z promocji mogą skorzystać wyłącznie osoby w wieku 16-26 lat. Pamiętaj o zabraniu dokumentu potwierdzającego Twój wiek."
                                />
                            </Info>
                        </Box>
                    )}

                    {isSpringPromoInCart && (
                        <Box mt={8} mb={16}>
                            <Info color="info">
                                <FormattedMessage
                                    id="order.discount.back_to_school_info"
                                    defaultMessage="Z promocji mogą skorzystać wyłącznie osoby w wieku 16-26 lat. Pamiętaj o zabraniu dokumentu potwierdzającego Twój wiek."
                                />
                            </Info>
                        </Box>
                    )}
                </ExpandableBox>
            ) : (
                <Box
                    direction="row"
                    isFullWidth
                    justify="space-between"
                    align="center"
                    gap={16}
                >
                    <CodeValid
                        promoCode={promoCode.toUpperCase()}
                        isLoading={isLoading}
                    />

                    {isCouponCodeProcessing ? (
                        <Loader />
                    ) : (
                        <Link
                            variant="secondary"
                            icon={null}
                            onClick={handleRemoveCoupon}
                        >
                            <FormattedMessage
                                id="order.discount.btn_remove_caption"
                                defaultMessage="Usuń kod"
                            />
                        </Link>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default PromoCode
